import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import { ChangeEvent, memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckBoxCircleFillIcon,
  DeleteBin6LineIcon,
  EditLineIcon,
  Information2LineIcon,
  InformationFillIcon,
  Refund2LineIcon,
  SortAscIcon,
  SortDescIcon,
} from '~/assets/icons';
import { IEquipment } from '~/utils/interface/equipment';
import styles from '../Equipment.module.scss';
import ServiceDialog from './service-dialog/ServiceDialog';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';
const cx = classNames.bind(styles);

type Props = {
  pageSize: number;
  equipments: IEquipment[];
  form: any;
  setRefreshData: (refresh: boolean) => void;
  onDetail?: (row: IEquipment) => void;
  onEdit?: (row: IEquipment) => void;
  onDelete?: (row: IEquipment) => void;
  equipmentIds?: Array<number>;
  handleCheck?: (row: IEquipment, checked: boolean) => void;
  handleCheckAll?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};

const EquipmentTable = (props: Props) => {
  const { t } = useTranslation('admin/equipment');

  const [isOpenServiceDialog, setIsOpenServiceDialog] = useState<boolean>(false);
  const [selectedEquipment, setSelectedEquipment] = useState<IEquipment | undefined>(undefined);
  const searchTimeout = useRef<any>(null);

  const getStatus = (value: any) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.notActivated')}
        </Box>
      );
    } else if (Number(value) === 3) {
      return (
        <Box className='text-[#F32A4C] border border-[#F32A4C] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.overDate')}
        </Box>
      );
    } else if (Number(value) === 2) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.dueDate')}
        </Box>
      );
    } else {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.valid')}
        </Box>
      );
    }
  };

  const handleSelectedOptions = (field: string, value: any) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      props.form.setFieldValue(field, value);
    }, SEARCH_TIMEOUT);
  };

  const handleSort = (field: string, type: string) => {
    props.form.setFieldValue('order_by', { field, type });
  };
  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
        maxHeight: '100vh',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          {' '}
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                onChange={props.handleCheckAll}
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            </TableCell>

            <TableCell className={cx('min-w-16', 'table-header')} align='center'>
              {t('no')}
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Typography className={cx('header-title')}>{t('imei')}</Typography>

                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'device_code' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('device_code', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('device_code', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.device_code}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('device_code', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box>{t('equipmentName')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'device_name' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('device_name', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('device_name', 'asc');
                    }}
                  />
                )}
              </Box>

              <TextField
                defaultValue={props.form.values.device_name}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('device_name', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('simNumber')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'sim_number' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('sim_number', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('sim_number', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.sim_no}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('sim_no', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('province2')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'province' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('province', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('province', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.province}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('province', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('ward')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'ward' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('ward', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('ward', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.ward}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('ward', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-36'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('addressDetail')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'address' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('address', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('address', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.address}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('address', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-24' align='center'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('connection')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-36' align='center'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('simStatus')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'sim_status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('sim_status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('sim_status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-36' align='center'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('iotStatus')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'iot_status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('iot_status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('iot_status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('agency')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'agent' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('agent', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('agent', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.agent_name}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('agent_name', e.target.value)}
              />
            </TableCell>
            <TableCell className='text-white border-l border-white min-w-32'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('npp')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'distributor' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('distributor', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('distributor', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.distributor_name}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('distributor_name', e.target.value)}
              />
            </TableCell>
            <TableCell className={cx('min-w-[120px]', 'table-header')} align='center'>
              {t('action')}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.equipments.map((row, index) => (
            <TableRow key={row.device_id}>
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={(props.equipmentIds ?? []).find((id) => id === row.device_id) ? true : false}
                  onChange={(event, checked) => props.handleCheck && props.handleCheck(row, checked)}
                  sx={{
                    color: '#005F73',
                    '&.Mui-checked': {
                      color: '#005F73',
                    },
                  }}
                />
              </TableCell>
              <TableCell align='center'>{index + 1 + (props.form.values.page_index - 1) * props.pageSize}</TableCell>
              <TableCell align='center'>{row.device_code}</TableCell>
              <TableCell align='center'>{row.device_name}</TableCell>
              <TableCell align='center'>{row.sim_no}</TableCell>
              <TableCell align='center'>{row.province}</TableCell>
              <TableCell align='center'>{row.ward}</TableCell>
              <TableCell align='center'>{row.address}</TableCell>
              <TableCell align='center'>
                <Box className='flex justify-center'>
                  {row.connect_status === 1 ? (
                    <CheckBoxCircleFillIcon
                      fill='#3D9149'
                      className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px]'
                    />
                  ) : (
                    <InformationFillIcon fill='#ED1002' className='lg:h-[15px] xl:h-[25px] lg:w-[15px] xl:w-[25px]' />
                  )}
                </Box>
              </TableCell>
              <TableCell align='center'>
                <Box className='flex justify-center'>{getStatus(Number(row.sim_status))}</Box>
              </TableCell>
              <TableCell align='center'>
                <Box className='flex justify-center'>{getStatus(Number(row.iot_status))}</Box>
              </TableCell>
              <TableCell align='center'>{row.agent}</TableCell>
              <TableCell align='center'>{row.distributor}</TableCell>
              <TableCell align='center'>
                <Box className='flex'>
                  <IconButton onClick={() => props?.onDetail && props.onDetail(row)}>
                    <Information2LineIcon fill='#72777B' />
                  </IconButton>
                  <IconButton onClick={() => props?.onEdit && props.onEdit(row)}>
                    <EditLineIcon />
                  </IconButton>
                  <IconButton>
                    <Refund2LineIcon
                      fill='#FF9500'
                      onClick={() => {
                        setSelectedEquipment(row);
                        setIsOpenServiceDialog(true);
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={() => props?.onDelete && props.onDelete(row)}>
                    <DeleteBin6LineIcon fill='#ED1002' />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ServiceDialog
        open={isOpenServiceDialog}
        setOpen={(isOpen) => setIsOpenServiceDialog(isOpen)}
        data={selectedEquipment}
      />
    </TableContainer>
  );
};

export default memo(EquipmentTable);
